'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { CHART_SERIES_COLORS } from '@/lib/browser-chart-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

// A picker wheel. Fairness note that the page copy repeats: the winner is
// chosen with crypto.getRandomValues BEFORE the animation starts, and the
// wheel is animated to land on that slice. The spin is theatre; the draw is
// uniform. Hue cycling is fine here, slices are positions, not identities.

const MAX_NAMES = 100;
const SPIN_MS = 4600;

const DEFAULT_NAMES = ['Alex', 'Sam', 'Maria', 'Chen', 'Priya', 'Omar'].join('\n');

function pickWinnerIndex(count: number): number {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return buffer[0] % count;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export default function BrowserWheelSpinner({
  config,
}: {
  config?: { defaultWheelItems?: string[]; toolSlug?: string };
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);

  const [namesText, setNamesText] = useState(
    config?.defaultWheelItems?.join('\n') ?? DEFAULT_NAMES,
  );
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [winner, setWinner] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const names = useMemo(
    () =>
      namesText
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .slice(0, MAX_NAMES),
    [namesText],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const size = 460;
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d');
    if (!context) return;

    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2 - 10;
    context.clearRect(0, 0, size, size);

    if (names.length < 2) return;

    const slice = (Math.PI * 2) / names.length;

    names.forEach((name, i) => {
      const start = rotation + i * slice;
      context.beginPath();
      context.moveTo(cx, cy);
      context.arc(cx, cy, radius, start, start + slice);
      context.closePath();
      context.fillStyle = CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length];
      context.fill();
      context.strokeStyle = '#ffffff';
      context.lineWidth = 2;
      context.stroke();

      // Label along the slice bisector, shrunk until it fits.
      const mid = start + slice / 2;
      context.save();
      context.translate(cx, cy);
      context.rotate(mid);
      context.textAlign = 'right';
      context.textBaseline = 'middle';
      const maxWidth = radius - 34;
      let fontSize = Math.min(20, Math.max(10, (slice * radius) / 2.2));
      context.font = `600 ${fontSize}px system-ui, sans-serif`;
      const label = name.length > 18 ? `${name.slice(0, 17)}…` : name;
      while (context.measureText(label).width > maxWidth && fontSize > 9) {
        fontSize -= 1;
        context.font = `600 ${fontSize}px system-ui, sans-serif`;
      }
      context.fillStyle = '#ffffff';
      context.fillText(label, radius - 16, 0);
      context.restore();
    });

    // Hub and pointer (pointer at 3 o'clock, pointing left into the wheel).
    context.beginPath();
    context.arc(cx, cy, 26, 0, Math.PI * 2);
    context.fillStyle = '#ffffff';
    context.fill();
    context.strokeStyle = '#d0d5da';
    context.stroke();

    context.beginPath();
    context.moveTo(size - 2, cy - 14);
    context.lineTo(size - 2, cy + 14);
    context.lineTo(size - 26, cy);
    context.closePath();
    context.fillStyle = '#1f2933';
    context.fill();
  }, [names, rotation]);

  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  async function spin() {
    if (isSpinning) return;
    if (names.length < 2) {
      setErrorMessage('Add at least two names, one per line.');
      return;
    }

    setErrorMessage(null);
    setCreditMessage(null);
    setWinner(null);
    setIsSpinning(true);

    let credits: Awaited<ReturnType<typeof consumeToolUsage>> | null = null;
    try {
      credits = await consumeToolUsage(
        config?.toolSlug ?? 'wheel-spinner',
        'Credits are not available for another spin right now.',
      );
    } catch (error) {
      setIsSpinning(false);
      setErrorMessage(error instanceof Error ? error.message : 'The wheel could not spin.');
      return;
    }

    // Draw first, then animate to the drawn slice: the pointer sits at angle 0
    // (3 o'clock), so the final rotation centers the winning slice there.
    const index = pickWinnerIndex(names.length);
    const slice = (Math.PI * 2) / names.length;
    const current = ((rotation % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    const targetWithinTurn =
      (Math.PI * 2 - (index * slice + slice / 2) + Math.PI * 2) % (Math.PI * 2);
    const fullTurns = Math.PI * 2 * (5 + Math.floor(Math.random() * 3));
    const delta = fullTurns + ((targetWithinTurn - current + Math.PI * 2) % (Math.PI * 2));

    const startRotation = rotation;
    const startTime = performance.now();
    const chosen = names[index];
    const creditNote = credits;

    const tick = (now: number) => {
      const t = Math.min(1, (now - startTime) / SPIN_MS);
      setRotation(startRotation + delta * easeOutCubic(t));
      if (t < 1) {
        animationRef.current = requestAnimationFrame(tick);
      } else {
        setIsSpinning(false);
        setWinner(chosen);
        setHistory((past) => [chosen, ...past].slice(0, 20));
        if (creditNote) setCreditMessage(formatToolCreditSuccess('Spin', creditNote));
      }
    };
    animationRef.current = requestAnimationFrame(tick);
  }

  function removeWinner() {
    if (!winner) return;
    setNamesText((current) =>
      current
        .split(/\r?\n/)
        .filter((line) => line.trim() !== winner)
        .join('\n'),
    );
    setWinner(null);
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Spin the wheel</h2>
          <p>
            Paste your names, spin, and get a fair winner. The draw uses the browser’s cryptographic
            random generator before the wheel even starts moving, and the names never leave your
            device.
          </p>
        </div>
        <span className="tool-live-badge">Wheel</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <canvas
            aria-label="Spinner wheel"
            className="tool-image-preview-canvas"
            ref={canvasRef}
            style={{ maxWidth: 460, background: 'transparent' }}
          />
          {winner && !isSpinning ? (
            <p aria-live="polite" className="tool-live-panel-note" style={{ fontSize: '1.15rem' }}>
              🎉 Winner: <strong>{winner}</strong>
            </p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <label>
            Names, one per line ({names.length}/{MAX_NAMES})
            <textarea
              onChange={(event) => {
                setNamesText(event.target.value);
                setWinner(null);
              }}
              rows={10}
              spellCheck={false}
              value={namesText}
            />
          </label>

          {winner && !isSpinning ? (
            <button className="button button-ghost" onClick={removeWinner} type="button">
              Remove winner and spin again
            </button>
          ) : null}

          {history.length > 1 ? (
            <div className="tool-live-stats">
              <div>
                <strong>Previous winners</strong>
                <span>{history.slice(1, 6).join(', ')}</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      <div className="hero-actions tool-live-actions">
        <button
          className="button"
          disabled={isSpinning || names.length < 2}
          onClick={() => void spin()}
          type="button"
        >
          {isSpinning ? 'Spinning…' : 'Spin the wheel'}
        </button>
      </div>

      {errorMessage ? (
        <p className="support-feedback support-feedback-error">{errorMessage}</p>
      ) : null}
      {creditMessage ? <p className="support-feedback">{creditMessage}</p> : null}
    </article>
  );
}
